import { useEffect, useMemo, useState } from "react";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { CometChatUIKitLoginListener } from "@cometchat/chat-uikit-react";
import { useChannels, useDirectMessages, Selection } from "./useChatData";
import { fetchVisibleUsers, emailForUid } from "./directoryScope";
import { parseMeta } from "./status";
import Avatar from "./Avatar";

type Props = {
  onClose: () => void;
  onSelect: (s: Selection) => void;
};

/** The search bar's "⌘K" behaviour — jump to any channel, DM, or partner by name. */
export default function QuickSwitcher({ onClose, onSelect }: Props) {
  const [query, setQuery] = useState("");
  const { groups } = useChannels();
  const { conversations } = useDirectMessages();
  const meUid = CometChatUIKitLoginListener.getLoggedInUser()?.getUid();

  // The people directory, already narrowed to whoever this user is
  // allowed to discover (see directoryScope.ts). For an internal team
  // member that's still everyone; for an external one it's their own
  // company plus DBM.
  const [allUsers, setAllUsers] = useState<CometChat.User[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetchVisibleUsers(meUid)
      .then((list) => {
        if (!cancelled) setAllUsers(list);
      })
      .catch((e) => console.warn("quick switcher user list failed", e));
    return () => {
      cancelled = true;
    };
  }, [meUid]);

  // Message-content search: kicks in from 3 characters, debounced so we
  // don't hammer the API on every keystroke.
  const [messageHits, setMessageHits] = useState<
    { key: string; snippet: string; where: string; selection: Selection }[]
  >([]);
  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) {
      setMessageHits([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      const meUid = CometChatUIKitLoginListener.getLoggedInUser()?.getUid();
      const req = new CometChat.MessagesRequestBuilder()
        .setLimit(20)
        .setSearchKeyword(q)
        .setCategories([CometChat.CATEGORY_MESSAGE])
        .setTypes(["text"])
        .build();
      req
        .fetchPrevious()
        .then((list) => {
          if (cancelled) return;
          const hits = ((list as CometChat.BaseMessage[]) || [])
            .filter((m) => m.getType() === "text")
            .reverse() // newest first
            .map((m) => {
              const text = (m as CometChat.TextMessage).getText() || "";
              const snippet = text.length > 70 ? text.slice(0, 67) + "…" : text;
              let selection: Selection;
              let where: string;
              if (m.getReceiverType() === "group") {
                const receiver = m.getReceiver() as CometChat.Group;
                const name = receiver?.getName?.() || m.getReceiverId();
                selection = { kind: "group", id: m.getReceiverId(), name, subtitle: "Channel" };
                where = `#${name}`;
              } else {
                const sender = m.getSender();
                const other =
                  sender?.getUid() === meUid
                    ? { id: m.getReceiverId(), name: (m.getReceiver() as CometChat.User)?.getName?.() || m.getReceiverId() }
                    : { id: sender?.getUid() || "", name: sender?.getName() || "Unknown" };
                selection = { kind: "user", id: other.id, name: other.name, subtitle: "Direct message" };
                where = other.name;
              }
              return { key: `hit-${m.getId()}`, snippet, where, selection };
            });
          setMessageHits(hits);
        })
        .catch((e) => {
          console.warn("message search failed", e);
          if (!cancelled) setMessageHits([]);
        });
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query]);

  const items = useMemo(() => {
    const channelItems = groups.map((g) => ({
      key: `group:${g.getGuid()}`,
      label: g.getName(),
      kind: "Channel",
      selection: { kind: "group" as const, id: g.getGuid(), name: g.getName(), subtitle: "Channel" },
    }));
    const dmItems = conversations.map((c) => {
      const other = c.getConversationWith() as import("@cometchat/chat-sdk-javascript").CometChat.User;
      return {
        key: `user:${other.getUid()}`,
        label: other.getName(),
        email: emailForUid(other.getUid()) || (parseMeta(other).dbmEmail as string | undefined),
        kind: "Direct message",
        selection: { kind: "user" as const, id: other.getUid(), name: other.getName(), subtitle: "Direct message" },
      };
    });
    // Everyone else in the directory — placed last so an existing DM (a
    // more specific label) always wins the de-dupe below.
    const peopleItems = allUsers.map((u) => ({
      key: `user:${u.getUid()}`,
      label: u.getName(),
      email: emailForUid(u.getUid()) || (parseMeta(u).dbmEmail as string | undefined),
      kind: "Person",
      selection: { kind: "user" as const, id: u.getUid(), name: u.getName(), subtitle: "Direct message" },
    }));

    const seen = new Set<string>();
    const all = [...channelItems, ...dmItems, ...peopleItems].filter((item) => {
      if (seen.has(item.key)) return false;
      seen.add(item.key);
      return true;
    });

    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        ("email" in item && item.email?.toLowerCase().includes(q))
    );
  }, [groups, conversations, allUsers, query]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <b>Jump to</b>
          <button className="hbtn" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-section">
          <div className="modal-row">
            <input
              type="text"
              autoFocus
              placeholder="Search people, channels, files…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>
        <div className="modal-section">
          <div className="modal-list">
            {items.length === 0 && messageHits.length === 0 && (
              <div className="sect-empty">No matches</div>
            )}
            {items.map((item) => (
              <button
                key={item.key}
                className="modal-user"
                onClick={() => {
                  onSelect(item.selection);
                  onClose();
                }}
              >
                {/* uid only for people — channels have no avatar to fetch. */}
                <Avatar
                  uid={item.selection.kind === "user" ? item.selection.id : undefined}
                  name={item.label}
                  size="dav"
                />
                {/* Email under the name: two people can share a display
                    name, and the address is the only thing that tells them
                    apart. It's already what the filter above matches on. */}
                <span className="qs-who">
                  <span className="nm">{item.label}</span>
                  {"email" in item && item.email && <span className="qs-email">{item.email}</span>}
                </span>
                <span className="ext" style={{ marginLeft: "auto" }}>{item.kind}</span>
              </button>
            ))}
          </div>
        </div>

        {messageHits.length > 0 && (
          <div className="modal-section">
            <div className="modal-label">Messages</div>
            <div className="modal-list">
              {messageHits.map((hit) => (
                <button
                  key={hit.key}
                  className="modal-user"
                  onClick={() => {
                    onSelect(hit.selection);
                    onClose();
                  }}
                >
                  <span className="nm hit-snippet">{hit.snippet}</span>
                  <span className="ext" style={{ marginLeft: "auto", flex: "none" }}>{hit.where}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
